import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import {
  formatDateRu,
  formatTimeRu,
  toDateInputValue,
} from "../../../utils/appointmentLabels";
import L from "../../../constants/baysportLabels";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import { HubScreen, HubFilterChips, HubTabScreen } from "../AdminPortal/HubPwa";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import {
  EmptyState,
  useToast,
} from "../../ui";
import { useHubBasePath, useHubScheduleUrl, hubClientsPath } from "../../../utils/hubPaths";
import AttendanceAbsenceSheet from "../AdminSchedule/AttendanceAbsenceSheet";
import AcceptSessionFeePayment from "../common/AcceptSessionFeePayment";
import CompetitionJournalResults from "./CompetitionJournalResults";
import JournalOfflineBanner from "./JournalOfflineBanner";
import { useJournalOffline } from "../../../hooks/useJournalOffline";
import { useActivation, ActivationPageGuide, ActTarget } from "../../../activation";
import { useTrainingMode } from "../../../training";
import {
  applyLocalAttendanceMark,
  cacheJournal,
  cacheSessions,
  enqueueMutation,
  readCachedJournal,
  readCachedSessions,
} from "../../../utils/journalOfflineStore";
import {
  attendanceMarkPayload,
  attendanceMarkState,
  attendanceStatusShort,
  canMarkSessionAttendance,
  rollCallStatsFromAttendances,
  sessionLabel,
  sessionSidebarLabel,
  sessionRollCallStats,
  monthGridDays,
} from "../AdminSchedule/calendarUtils";

function memberInitials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function visitsLabel(att) {
  const m = att.membership;
  if (!m) return null;
  if (m.status === "pending") {
    const name = m.plan?.name || "абонемент";
    return `${name} · не оплачен`;
  }
  if (m.plan?.plan_type === "visits" && m.visits_remaining != null) {
    return `${m.visits_remaining} зан.`;
  }
  if (m.ends_at) return `до ${m.ends_at}`;
  return "безлимит";
}

function membershipWarnLabel(warn) {
  if (warn === "unpaid") return "Должник";
  if (warn === "low_visits") return "Мало занятий";
  if (warn === "expired") return "Абонемент истёк";
  if (warn === "frozen") return "Заморожен";
  if (warn === "none") return "Нет абонемента";
  return null;
}

const AVATAR_TONES = [
  "#e0e7ff",
  "#dbeafe",
  "#dcfce7",
  "#fef3c7",
  "#fce7f3",
  "#ede9fe",
];

function avatarTone(id) {
  const s = String(id || "0");
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n += s.charCodeAt(i);
  return AVATAR_TONES[n % AVATAR_TONES.length];
}

function shiftDateInput(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return toDateInputValue(d);
}

function weekDatesForAnchor(dateStr) {
  const anchor = new Date(`${dateStr}T12:00:00`);
  const dow = anchor.getDay();
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + (dow === 0 ? -6 : 1 - dow));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return toDateInputValue(d);
  });
}

function formatJournalHeader(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return {
    title: d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" }),
    meta: `${d.getFullYear()} · ${d.toLocaleDateString("ru-RU", {
      weekday: "long",
    })}`,
  };
}

function JournalNavChevron({ direction }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={direction === "left" ? "M14.5 6.5L9 12l5.5 5.5" : "M9.5 6.5L15 12l-5.5 5.5"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function weekMarkersFromSessions(sessions) {
  const has = new Set();
  const needs = new Set();
  for (const sess of sessions) {
    if (sess.status === "cancelled") continue;
    if (sess.session_kind === "event") continue;
    const dayIso = (sess.start_datetime || "").slice(0, 10);
    if (!dayIso) continue;
    has.add(dayIso);
    if (sess.needs_attendance) needs.add(dayIso);
  }
  return { has, needs };
}

function JournalSessionCard({
  sess,
  onOpen,
  isLast,
  active = false,
  desktop = false,
  liveStats = null,
}) {
  const { total, present, marked, allMarked, allPresent } =
    liveStats || sessionRollCallStats(sess);
  const needs = Boolean(sess.needs_attendance);

  const statusClass = allPresent
    ? "done"
    : allMarked
      ? "muted"
      : needs
        ? "warn"
        : marked > 0
          ? "muted"
          : "empty";

  const mobileStatusClass =
    statusClass === "done"
      ? aa.journalSessionBadgeDone
      : statusClass === "warn"
        ? aa.journalSessionBadgeWarn
        : statusClass === "muted"
          ? aa.journalSessionBadgeMuted
          : aa.journalSessionBadgeEmpty;

  const desktopBadgeClass =
    statusClass === "done"
      ? md.journalDeskBadgeDone
      : statusClass === "warn"
        ? md.journalDeskBadgeWarn
        : statusClass === "muted"
          ? md.journalDeskBadgeMuted
          : md.journalDeskBadgeEmpty;

  const timeRange = `${formatTimeRu(sess.start_datetime)} – ${formatTimeRu(sess.end_datetime)}`;
  const meta = [
    sess.branch?.name,
    sess.trainer?.user?.full_name || sess.trainer?.full_name,
  ]
    .filter(Boolean)
    .join(" · ");

  if (desktop) {
    return (
      <ActTarget stepId="attendance" subId="att_open" as="div">
      <button
        type="button"
        className={`${md.journalDeskSession} ${needs ? md.journalDeskSessionNeeds : ""} ${active ? md.journalDeskSessionActive : ""}`}
        onClick={onOpen}
        aria-pressed={active}
      >
        <div className={md.journalDeskSessionTop}>
          <span className={md.journalDeskSessionTime}>{timeRange}</span>
          <span
            className={`${md.journalDeskSessionBadge} ${desktopBadgeClass}`}
            title={
              total
                ? `Отмечено ${marked}, было ${present} из ${total}`
                : undefined
            }
          >
            {total ? `${marked}/${total}` : "—"}
          </span>
        </div>
        <span className={md.journalDeskSessionTitle}>
          {sessionSidebarLabel(sess)}
        </span>
        {meta ? (
          <span className={md.journalDeskSessionMeta}>{meta}</span>
        ) : null}
      </button>
      </ActTarget>
    );
  }

  return (
    <ActTarget stepId="attendance" subId="att_open" as="div">
    <button
      type="button"
      className={`${aa.journalSessionCard} ${needs ? aa.journalSessionCardNeeds : ""} ${active ? aa.journalSessionCardActive : ""} ${isLast ? aa.journalSessionCardLast : ""}`}
      onClick={onOpen}
      aria-pressed={active}
    >
      <div className={aa.journalSessionTimeCol}>
        <span>{formatTimeRu(sess.start_datetime)}</span>
        <span className={aa.journalSessionTimeSep} aria-hidden>
          –
        </span>
        <span className={aa.journalSessionTimeEnd}>
          {formatTimeRu(sess.end_datetime)}
        </span>
      </div>
      <div className={aa.journalSessionBody}>
        <span className={aa.journalSessionTitle}>
          {sessionSidebarLabel(sess)}
        </span>
        <span className={aa.journalSessionMeta}>{meta}</span>
      </div>
      <span
        className={`${aa.journalSessionBadge} ${mobileStatusClass}`}
        title={
          total
            ? `Отмечено ${marked}, было ${present} из ${total}`
            : undefined
        }
      >
        {total ? `${marked}/${total}` : "—"}
      </span>
      <span className={aa.journalSessionChevron} aria-hidden />
    </button>
    </ActTarget>
  );
}

function JournalCalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 3v2M16 3v2M5 9h14M6 5h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function JournalWeekPicker({
  date,
  onPick,
  daysWithSessions,
  daysNeedingAttendance,
  desktop = false,
}) {
  const today = toDateInputValue();
  const week = weekDatesForAnchor(date);
  const header = formatJournalHeader(date);
  const prevWeek = shiftDateInput(date, -7);
  const nextWeek = shiftDateInput(date, 7);

  const headBlock = (
    <div className={desktop ? md.desktopJournalCalHead : aa.journalDateHead}>
      <div
        className={
          desktop ? md.desktopJournalCalHeadText : aa.journalDateHeadText
        }
      >
        <h2
          className={
            desktop ? md.desktopJournalCalTitle : aa.journalMonthTitle
          }
        >
          {header.title}
        </h2>
        <p
          className={desktop ? md.desktopJournalCalMeta : aa.journalMonthMeta}
        >
          {header.meta}
        </p>
      </div>
      <div
        className={
          desktop ? md.desktopJournalCalActions : aa.journalDateHeadActions
        }
      >
        {date !== today ? (
          <button
            type="button"
            className={desktop ? md.desktopJournalCalToday : aa.journalTodayLink}
            onClick={() => onPick(today)}
          >
            Сегодня
          </button>
        ) : null}
        <label
          className={
            desktop ? md.desktopJournalCalBtn : aa.journalDateCalendarBtn
          }
          aria-label="Выбрать дату"
        >
          <JournalCalendarIcon />
          <input
            type="date"
            value={date}
            onChange={(e) => onPick(e.target.value)}
          />
        </label>
      </div>
    </div>
  );

  const weekBlock = (
    <div className={desktop ? md.desktopJournalCalWeek : aa.journalWeekRow}>
      <button
        type="button"
        className={desktop ? md.desktopJournalCalNav : aa.journalWeekNav}
        onClick={() => onPick(prevWeek)}
        aria-label="Предыдущая неделя"
      >
        <JournalNavChevron direction="left" />
      </button>
      <div
        className={desktop ? md.desktopJournalCalStrip : aa.journalWeekStrip}
        role="group"
        aria-label="Дни недели"
      >
        {week.map((dayIso) => {
          const d = new Date(`${dayIso}T12:00:00`);
          const active = dayIso === date;
          const isToday = dayIso === today;
          const wd = d
            .toLocaleDateString("ru-RU", { weekday: "short" })
            .replace(/\./g, "")
            .slice(0, 2);
          const hasSessions = daysWithSessions?.has(dayIso);
          const needsMark = daysNeedingAttendance?.has(dayIso);
          let dotClass = desktop ? md.desktopJournalCalDot : aa.journalDayDot;
          if (active) {
            dotClass = desktop
              ? md.desktopJournalCalDotOn
              : aa.journalDayDotOn;
          } else if (needsMark) {
            dotClass = desktop
              ? md.desktopJournalCalDotWarn
              : aa.journalDayDotWarn;
          } else if (hasSessions) {
            dotClass = desktop
              ? md.desktopJournalCalDotHas
              : aa.journalDayDotHas;
          }
          const dayClass = desktop
            ? [
                md.desktopJournalCalDay,
                active ? md.desktopJournalCalDayOn : "",
                isToday && !active ? md.desktopJournalCalDayToday : "",
              ]
                .filter(Boolean)
                .join(" ")
            : active
              ? `${aa.journalDay} ${aa.journalDayOn}`
              : aa.journalDay;
          return (
            <button
              key={dayIso}
              type="button"
              className={dayClass}
              onClick={() => onPick(dayIso)}
              aria-pressed={active}
              aria-label={formatDateRu(`${dayIso}T12:00:00`, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            >
              <span
                className={
                  desktop ? md.desktopJournalCalDayWd : aa.journalDayWd
                }
              >
                {wd}
              </span>
              <span
                className={
                  desktop ? md.desktopJournalCalDayNum : aa.journalDayNum
                }
              >
                {d.getDate()}
              </span>
              {hasSessions || (isToday && !active) ? (
                <span className={dotClass} aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={desktop ? md.desktopJournalCalNav : aa.journalWeekNav}
        onClick={() => onPick(nextWeek)}
        aria-label="Следующая неделя"
      >
        <JournalNavChevron direction="right" />
      </button>
    </div>
  );

  if (desktop) {
    return (
      <>
        {headBlock}
        {weekBlock}
      </>
    );
  }

  return (
    <div className={aa.journalDateSection}>
      {headBlock}
      {weekBlock}
    </div>
  );
}

const JOURNAL_MINI_WD = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

function JournalMiniMonth({
  date,
  onPick,
  daysWithSessions,
  daysNeedingAttendance,
}) {
  const today = toDateInputValue();
  const monthDays = monthGridDays(date);
  const monthKey = date.slice(0, 7);
  const monthTitle = new Date(`${monthKey}-01T12:00:00`).toLocaleDateString(
    "ru-RU",
    { month: "long", year: "numeric" },
  );

  const shiftMonth = (delta) => {
    const d = new Date(`${monthKey}-01T12:00:00`);
    d.setMonth(d.getMonth() + delta);
    const prevDay = Number(date.slice(8, 10));
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const newDay = String(Math.min(prevDay, lastDay)).padStart(2, "0");
    onPick(`${toDateInputValue(d).slice(0, 7)}-${newDay}`);
  };

  return (
    <div className={md.desktopJournalMiniSection}>
      <div className={md.desktopJournalMiniHead}>
        <button
          type="button"
          className={md.desktopJournalCalNav}
          onClick={() => shiftMonth(-1)}
          aria-label="Предыдущий месяц"
        >
          <JournalNavChevron direction="left" />
        </button>
        <p className={md.desktopJournalMiniTitle}>{monthTitle}</p>
        <button
          type="button"
          className={md.desktopJournalCalNav}
          onClick={() => shiftMonth(1)}
          aria-label="Следующий месяц"
        >
          <JournalNavChevron direction="right" />
        </button>
      </div>
      {date !== today ? (
        <button
          type="button"
          className={md.desktopJournalMiniToday}
          onClick={() => onPick(today)}
        >
          Сегодня
        </button>
      ) : null}
      <div className={md.desktopJournalMini}>
        <div className={md.desktopJournalMiniWeekdays}>
          {JOURNAL_MINI_WD.map((w) => (
            <div key={w} className={md.desktopJournalMiniWeekday}>
              {w}
            </div>
          ))}
        </div>
        <div className={md.desktopJournalMiniGrid}>
          {monthDays.map((dayIso) => {
            const inMonth = dayIso.slice(0, 7) === monthKey;
            const active = dayIso === date;
            const isToday = dayIso === today;
            const hasSessions = daysWithSessions?.has(dayIso);
            const needsMark = daysNeedingAttendance?.has(dayIso);
            return (
              <button
                key={dayIso}
                type="button"
                className={[
                  md.desktopJournalMiniCell,
                  !inMonth ? md.desktopJournalMiniCellMuted : "",
                  isToday ? md.desktopJournalMiniCellToday : "",
                  active ? md.desktopJournalMiniCellSelected : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => onPick(dayIso)}
                aria-pressed={active}
                aria-label={dayIso}
              >
                <span className={md.desktopJournalMiniDayNum}>
                  {Number(dayIso.slice(8, 10))}
                </span>
                {needsMark ? (
                  <span className={md.desktopJournalMiniDotWarn} aria-hidden />
                ) : hasSessions ? (
                  <span className={md.desktopJournalMiniDot} aria-hidden />
                ) : (
                  <span className={md.desktopJournalMiniDotSpacer} aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function JournalCompetitionView({ journal, onResultsChange }) {
  const summary = journal.competition_registration_summary || {};
  const approved = summary.approved ?? 0;
  const pending = summary.pending ?? 0;
  const rejected = summary.rejected ?? 0;
  const placements = summary.podium ?? summary.placements ?? 0;
  const participated = summary.participated ?? 0;
  const noShows = summary.no_shows ?? 0;
  const unmarked = summary.unmarked ?? Math.max(0, approved - placements - participated - noShows);

  return (
    <div className={aa.journalAttendPage}>
      <div className={aa.journalCompetitionHeroCard}>
        <span className={aa.journalCompetitionBadge}>Соревнование</span>
        <h2 className={aa.journalCompetitionTitle}>
          {journal.title || "Соревнование"}
        </h2>
        <p className={aa.journalCompetitionTime}>
          {formatTimeRu(journal.start_datetime)} —{" "}
          {formatTimeRu(journal.end_datetime)}
        </p>
        <p className={aa.journalCompetitionMeta}>
          {[
            journal.branch?.name,
            journal.trainer?.user?.full_name || journal.trainer?.full_name,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>

      <div className={aa.journalCompetitionStats}>
        <div
          className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatApproved}`}
        >
          <strong>{approved}</strong>
          <span>допущено</span>
        </div>
        <div className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatResults}`}>
          <strong>{placements}</strong>
          <span>призовые</span>
        </div>
        {participated > 0 ? (
          <div className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatParticipated}`}>
            <strong>{participated}</strong>
            <span>участвовали</span>
          </div>
        ) : null}
        <div className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatNoShow}`}>
          <strong>{noShows}</strong>
          <span>не пришли</span>
        </div>
        {unmarked > 0 ? (
          <div className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatPending}`}>
            <strong>{unmarked}</strong>
            <span>без отметки</span>
          </div>
        ) : null}
        {pending > 0 ? (
          <div
            className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatPending}`}
          >
            <strong>{pending}</strong>
            <span>на рассмотрении</span>
          </div>
        ) : null}
        {rejected > 0 ? (
          <div
            className={`${aa.journalCompetitionStat} ${aa.journalCompetitionStatRejected}`}
          >
            <strong>{rejected}</strong>
            <span>не допущен</span>
          </div>
        ) : null}
      </div>

      <div className={aa.journalCompetitionBody}>
        <CompetitionJournalResults
          sessionId={journal.id}
          onUpdated={onResultsChange}
        />
      </div>
    </div>
  );
}

function JournalAttendanceView({
  journal,
  mark,
  markDisabled,
  onAbsence,
  onMarkAll,
  onMarkAllAbsent,
  markingAll,
  markingPatientId,
  onCompetitionResultsChange,
  onSessionFeePaid,
  desktop = false,
  guideEmptySkip = null,
}) {
  const hubBase = useHubBasePath();
  const touchStart = useRef({ x: 0, y: 0, id: null });

  if (!journal) return null;

  const isCompetition =
    journal.session_kind === "event" && journal.event_kind === "competition";

  if (isCompetition) {
    return (
      <JournalCompetitionView
        journal={journal}
        onResultsChange={onCompetitionResultsChange}
      />
    );
  }

  const attendances = journal.attendances || [];
  const declinedAttendances = journal.declined_attendances || [];
  const unbookedMembers = journal.unbooked_group_members || [];
  const bookingMode = Boolean(
    journal.booking_mode || Number(journal.rsvp_capacity) > 0,
  );
  const groupArchived = Boolean(
    journal.group_archived || journal.group?.is_active === false,
  );
  const emptyTitle = bookingMode
    ? "Пока никто не записался"
    : groupArchived
      ? "Нет сохранённых отметок"
      : "В группе никого нет";
  const emptyDescription = bookingMode
    ? "Клиенты жмут «Приду» в приложении. Пришедших без записи отметьте ниже."
    : groupArchived
      ? "Группа в архиве. Отметки, сделанные до архива, отображаются здесь."
      : guideEmptySkip
        ? "Добавьте ученика в группу — или пропустите этот шаг обучения."
        : "Добавьте участников в карточке группы";
  const emptyBlock = (
    <>
      <EmptyState title={emptyTitle} description={emptyDescription} />
      {guideEmptySkip && !bookingMode && !groupArchived ? (
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            type="button"
            className={desktop ? md.desktopJournalPlaceholderBtn : aa.journalHint}
            onClick={guideEmptySkip}
            style={
              desktop
                ? undefined
                : {
                    display: "inline-block",
                    padding: "10px 16px",
                    border: "1px solid currentColor",
                    borderRadius: 10,
                    background: "transparent",
                    cursor: "pointer",
                  }
            }
          >
            Пропустить журнал
          </button>
        </div>
      ) : null}
    </>
  );
  const roll = rollCallStatsFromAttendances(attendances, journal);
  const present = roll.present;
  const marked = roll.marked;
  const expected = roll.total || attendances.length;
  const canMark = !markDisabled;
  const rowBusy = (id) => markingAll || markingPatientId === id;
  const needsPresentMark =
    expected > 0 && attendances.some((a) => !attendanceMarkState(a).isPresent);
  const needsAbsentMark = attendances.some(
    (a) => !attendanceMarkState(a).isAbsent,
  );
  const guidePresentTargetId = attendances.find(
    (a) => !attendanceMarkState(a).isPresent,
  )?.patient_id;
  const sessionMeta = [
    journal.branch?.name,
    journal.trainer?.user?.full_name || journal.trainer?.full_name,
    bookingMode && journal.rsvp_capacity
      ? `запись ${journal.rsvp_coming_count ?? 0}/${journal.rsvp_capacity}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const bookedSectionTitle = bookingMode ? (
    <p
      className={
        desktop ? md.journalDeskBookedTitle : aa.journalBookedTitle
      }
    >
      {journal.rsvp_capacity
        ? `Записались · ${journal.rsvp_coming_count ?? attendances.length} из ${journal.rsvp_capacity}`
        : `Записались · ${attendances.length}`}
    </p>
  ) : null;

  const renderAttendRow = (a) => {
    const { marked, isPresent, isAbsent, isAbsentOnly } =
      attendanceMarkState(a);
    const warnText = membershipWarnLabel(a.membership_warning);
    const membershipMeta = marked ? visitsLabel(a) : null;
    const showChargeInMeta =
      marked &&
      isPresent &&
      a.charge_error &&
      !["none", "expired", "unpaid", "frozen"].includes(a.membership_warning);
    const hasError = isPresent && a.charge_error;
    const needsSessionFee =
      isPresent &&
      a.session_fee_status === "due" &&
      !a.visit_charged;
    const rowClass = desktop
      ? [
          md.journalDeskRow,
          hasError || needsSessionFee ? md.journalDeskRowError : "",
          !marked ? md.journalDeskRowUnmarked : "",
        ]
          .filter(Boolean)
          .join(" ")
      : hasError || needsSessionFee
        ? `${aa.journalAttendRow} ${aa.journalAttendRowError}`
        : !marked
          ? `${aa.journalAttendRow} ${aa.journalAttendRowUnmarked}`
          : aa.journalAttendRow;
    const avatarClass = desktop ? md.journalDeskAvatar : aa.journalAttendAvatar;
    const infoClass = desktop ? md.journalDeskInfo : aa.journalAttendInfo;
    const toggleClass = desktop ? md.journalDeskToggle : aa.journalAttendToggle;
    const toggleBtn = (extra) =>
      desktop ? `${md.journalDeskToggleBtn} ${extra}` : `${aa.journalAttendToggleBtn} ${extra}`;

    return (
      <li
        key={a.id}
        className={rowClass}
        onTouchStart={
          desktop
            ? undefined
            : (e) => {
                if (!canMark) return;
                const t = e.touches[0];
                touchStart.current = {
                  x: t.clientX,
                  y: t.clientY,
                  id: a.patient_id,
                };
              }
        }
        onTouchEnd={
          desktop
            ? undefined
            : (e) => {
                if (!canMark) return;
                const t = e.changedTouches[0];
                const dx = t.clientX - touchStart.current.x;
                if (touchStart.current.id !== a.patient_id) return;
                if (
                  dx < -60 &&
                  Math.abs(t.clientY - touchStart.current.y) < 40
                ) {
                  onAbsence({
                    patientId: a.patient_id,
                    name: a.patient?.full_name,
                  });
                }
              }
        }
      >
        <div
          className={avatarClass}
          style={{ background: avatarTone(a.patient_id) }}
          aria-hidden
        >
          {memberInitials(a.patient?.full_name)}
        </div>
        <div className={infoClass}>
          <div className={desktop ? md.journalDeskNameRow : aa.journalAttendNameRow}>
            <Link to={hubClientsPath(hubBase, a.patient_id)}>
              {a.patient?.full_name || "—"}
            </Link>
          </div>
          <span className={desktop ? md.journalDeskMeta : aa.journalAttendMeta}>
            {attendanceStatusShort(a)}
            {membershipMeta ? ` · ${membershipMeta}` : ""}
            {showChargeInMeta ? ` · ${a.charge_error}` : ""}
            {a.session_fee_status === "paid" ? " · занятие оплачено" : ""}
            {a.session_fee_status === "waived" ? " · долг списан" : ""}
          </span>
          {warnText ? (
            <span
              className={
                a.membership_warning === "unpaid"
                  ? `${aa.journalAttendWarn} ${aa.journalAttendWarnUnpaid}`
                  : aa.journalAttendWarn
              }
            >
              {warnText}
            </span>
          ) : null}
          {a.medical_warning === "expired" ? (
            <span className={aa.journalAttendWarn}>Допуск просрочен</span>
          ) : null}
          {needsSessionFee ? (
            <AcceptSessionFeePayment
              attendance={a}
              onSuccess={() => onSessionFeePaid?.(a)}
            />
          ) : null}
        </div>
        <div
          className={toggleClass}
          role="group"
          aria-label="Посещаемость"
        >
          {guidePresentTargetId &&
          String(guidePresentTargetId) === String(a.patient_id) ? (
            <ActTarget stepId="attendance" subId="att_mark" as="span" compact>
              <button
                type="button"
                className={`${toggleBtn(desktop ? md.journalDeskTogglePresent : aa.journalAttendTogglePresent)} ${
                  isPresent
                    ? desktop
                      ? md.journalDeskToggleOn
                      : aa.journalAttendToggleOn
                    : ""
                }`}
                disabled={!canMark || rowBusy(a.patient_id)}
                onClick={(e) => {
                  e.stopPropagation();
                  mark(a.patient_id, "present", undefined, a);
                }}
                onTouchStart={(e) => e.stopPropagation()}
                aria-pressed={isPresent}
                aria-label="Был"
              >
                ✓
              </button>
            </ActTarget>
          ) : (
            <button
              type="button"
              className={`${toggleBtn(desktop ? md.journalDeskTogglePresent : aa.journalAttendTogglePresent)} ${
                isPresent
                  ? desktop
                    ? md.journalDeskToggleOn
                    : aa.journalAttendToggleOn
                  : ""
              }`}
              disabled={!canMark || rowBusy(a.patient_id)}
              onClick={(e) => {
                e.stopPropagation();
                mark(a.patient_id, "present", undefined, a);
              }}
              onTouchStart={(e) => e.stopPropagation()}
              aria-pressed={isPresent}
              aria-label="Был"
            >
              ✓
            </button>
          )}
          <button
            type="button"
            className={`${toggleBtn(desktop ? md.journalDeskToggleAbsent : aa.journalAttendToggleAbsent)} ${
              isAbsent
                ? desktop
                  ? md.journalDeskToggleOn
                  : aa.journalAttendToggleOn
                : ""
            }`}
            disabled={!canMark || rowBusy(a.patient_id)}
            onClick={(e) => {
              e.stopPropagation();
              if (isAbsentOnly) {
                mark(a.patient_id, "absent", undefined, a);
                return;
              }
              onAbsence({
                patientId: a.patient_id,
                name: a.patient?.full_name,
              });
            }}
            onTouchStart={(e) => e.stopPropagation()}
            aria-pressed={isAbsent}
            aria-label="Не был"
          >
            ✕
          </button>
        </div>
      </li>
    );
  };

  const unbookedBlock =
    bookingMode && unbookedMembers.length > 0 ? (
      <div
        className={
          desktop ? md.journalDeskUnbooked : aa.journalUnbookedBlock
        }
      >
        <p
          className={
            desktop ? md.journalDeskUnbookedTitle : aa.journalUnbookedTitle
          }
        >
          Не записались · {unbookedMembers.length}
        </p>
        <ul
          className={
            desktop ? md.journalDeskAttendList : aa.journalAttendList
          }
        >
          {unbookedMembers.map((m) => {
            if (m.attendance) {
              return renderAttendRow({
                ...m.attendance,
                patient: m.attendance.patient || {
                  id: m.patient_id,
                  full_name: m.full_name,
                },
              });
            }
            return (
              <li
                key={m.patient_id}
                className={
                  desktop
                    ? `${md.journalDeskRow} ${md.journalDeskRowUnmarked}`
                    : `${aa.journalAttendRow} ${aa.journalAttendRowUnmarked}`
                }
              >
                <div
                  className={
                    desktop ? md.journalDeskAvatar : aa.journalAttendAvatar
                  }
                  style={{ background: avatarTone(m.patient_id) }}
                  aria-hidden
                >
                  {memberInitials(m.full_name)}
                </div>
                <div
                  className={desktop ? md.journalDeskInfo : aa.journalAttendInfo}
                >
                  <div
                    className={
                      desktop ? md.journalDeskNameRow : aa.journalAttendNameRow
                    }
                  >
                    <Link to={hubClientsPath(hubBase, m.patient_id)}>
                      {m.full_name}
                    </Link>
                  </div>
                  <span
                    className={
                      desktop ? md.journalDeskMeta : aa.journalAttendMeta
                    }
                  >
                    Без «Приду»
                  </span>
                </div>
                {canMark ? (
                  <div
                    className={
                      desktop ? md.journalDeskToggle : aa.journalAttendToggle
                    }
                  >
                    <button
                      type="button"
                      className={
                        desktop
                          ? md.journalDeskToggleBtn
                          : aa.journalAttendToggleBtn
                      }
                      disabled={rowBusy(m.patient_id)}
                      onClick={() => mark(m.patient_id, "present")}
                    >
                      {rowBusy(m.patient_id) ? "…" : "Был"}
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

  if (desktop) {
    return (
      <div className={md.journalDeskAttend}>
        <header className={md.journalDeskAttendHead}>
          <div className={md.journalDeskAttendHeadMain}>
            <h2 className={md.journalDeskAttendTitle}>{sessionLabel(journal)}</h2>
            <p className={md.journalDeskAttendTime}>
              {formatTimeRu(journal.start_datetime)} –{" "}
              {formatTimeRu(journal.end_datetime)}
            </p>
            {sessionMeta ? (
              <p className={md.journalDeskAttendMeta}>{sessionMeta}</p>
            ) : null}
            {expected > 0 ? (
              <p className={md.journalDeskAttendStat}>
                Перекличка {marked}/{expected}
                {present > 0
                  ? ` · было ${present}`
                  : marked === 0
                    ? " · нет отметок"
                    : ""}
              </p>
            ) : null}
          </div>
          {canMark ? (
            <div className={md.journalDeskAttendActions}>
              <details className={md.journalDeskHelp}>
                <summary>Подсказка</summary>
                <p>
                  «Был» и «Не был» (с причиной) — оба считаются в перекличке.
                  Повторный клик снимает отметку. «Все отсутствуют» — одна
                  причина на группу, потом можно поставить «Был» тем, кто
                  пришёл.
                </p>
              </details>
              {needsPresentMark || needsAbsentMark ? (
                <div className={md.journalDeskAttendBulk}>
                  {needsPresentMark ? (
                      <button
                        type="button"
                        className={md.journalDeskMarkAllBtn}
                        disabled={markingAll}
                        onClick={onMarkAll}
                      >
                        {markingAll ? "…" : "Все были"}
                      </button>
                  ) : null}
                  {needsAbsentMark ? (
                    <button
                      type="button"
                      className={md.journalDeskMarkAllAbsentBtn}
                      disabled={markingAll}
                      onClick={onMarkAllAbsent}
                    >
                      Все отсутствуют
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </header>

        {!canMark ? (
          <p className={md.journalDeskCancelled}>
            {journal.journal_readonly
              ? "Чужое занятие — только просмотр"
              : "Занятие отменено — отметка недоступна"}
          </p>
        ) : null}

        <div className={md.journalDeskAttendBody}>
          {bookedSectionTitle}
          {attendances.length === 0 ? (
            emptyBlock
          ) : (
            <ul className={md.journalDeskAttendList}>
              {attendances.map(renderAttendRow)}
            </ul>
          )}
          {bookingMode && declinedAttendances.length > 0 ? (
            <div className={md.journalDeskDeclined}>
              <p className={md.journalDeskDeclinedTitle}>
                Не придут · {declinedAttendances.length}
              </p>
              <ul className={md.journalDeskAttendList}>
                {declinedAttendances.map(renderAttendRow)}
              </ul>
            </div>
          ) : null}
          {unbookedBlock}
        </div>
      </div>
    );
  }

  return (
    <div className={aa.journalAttendPage}>
      <div className={aa.journalAttendHero}>
        <p className={aa.journalAttendHeroTime}>
          {formatTimeRu(journal.start_datetime)} —{" "}
          {formatTimeRu(journal.end_datetime)}
        </p>
        <p className={aa.journalAttendHeroMeta}>
          {[
            journal.branch?.name,
            journal.trainer?.user?.full_name || journal.trainer?.full_name,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {expected > 0 ? (
          <p className={aa.journalAttendHeroStat}>
            Перекличка {marked}/{expected}
            {present > 0 ? ` · было ${present}` : marked === 0 ? " · нет отметок" : ""}
          </p>
        ) : null}
      </div>

      {canMark ? (
        <div className={aa.journalAttendToolbar}>
          <details className={aa.journalAttendHelp}>
            <summary>Подсказка</summary>
            <p>
              «Был» и «Не был» (с причиной) — оба считаются в перекличке.
              Повторный тап снимает отметку и возвращает занятие на абонемент.
              «Все отсутствуют» — одна причина на группу, потом можно поставить
              «Был» тем, кто пришёл.
            </p>
          </details>
          {needsPresentMark || needsAbsentMark ? (
            <div className={aa.journalAttendBulk}>
              {needsPresentMark ? (
                  <button
                    type="button"
                    className={aa.journalMarkAllBtn}
                    disabled={markingAll}
                    onClick={onMarkAll}
                  >
                    {markingAll ? "…" : "Все были"}
                  </button>
              ) : null}
              {needsAbsentMark ? (
                <button
                  type="button"
                  className={aa.journalMarkAllAbsentBtn}
                  disabled={markingAll}
                  onClick={onMarkAllAbsent}
                >
                  Все отсутствуют
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <p className={aa.journalAttendCancelled}>Занятие отменено — отметка недоступна</p>
      )}

      {bookedSectionTitle}
      {attendances.length === 0 ? (
        emptyBlock
      ) : (
        <ul className={aa.journalAttendList}>
          {attendances.map(renderAttendRow)}
        </ul>
      )}
      {bookingMode && declinedAttendances.length > 0 ? (
        <div className={aa.journalDeclinedBlock}>
          <p className={aa.journalDeclinedTitle}>
            Не придут · {declinedAttendances.length}
          </p>
          <ul className={aa.journalAttendList}>
            {declinedAttendances.map(renderAttendRow)}
          </ul>
        </div>
      ) : null}
      {unbookedBlock}
    </div>
  );
}

export default function AdminJournal({ staffMode = false }) {
  const toast = useToast();
  const {
    refresh: refreshActivation,
    visible: activationVisible,
    currentStep,
    currentSubstepId,
    setSubstep,
    advanceSubstep,
    finishCurrentStep,
    skipStep,
    completed: activationCompleted,
  } = useActivation();
  const {
    active: trainingActive,
    currentLesson,
    trainingSubstepId,
    advanceTrainingSubstep,
    setTrainingSubstep,
    completed: trainingCompleted,
  } = useTrainingMode();
  const attendanceGuideActive =
    (activationVisible && currentStep?.id === "attendance") ||
    (trainingActive && currentLesson?.id === "attendance");
  const attendanceSubstepId = trainingActive
    ? trainingSubstepId
    : currentSubstepId;
  const attendanceGuideDone =
    (trainingActive && trainingCompleted.includes("attendance")) ||
    (activationVisible && activationCompleted.includes("attendance"));
  const finishAttendanceMarkGuide = useCallback(async () => {
    if (
      !attendanceGuideActive ||
      attendanceGuideDone ||
      attendanceSubstepId !== "att_mark"
    ) {
      return;
    }
    if (trainingActive) {
      advanceTrainingSubstep();
      return;
    }
    const status = await refreshActivation?.();
    finishCurrentStep?.(status);
  }, [
    advanceTrainingSubstep,
    attendanceGuideActive,
    attendanceGuideDone,
    attendanceSubstepId,
    finishCurrentStep,
    refreshActivation,
    trainingActive,
  ]);

  const skipEmptyAttendanceGuide = useCallback(() => {
    if (!attendanceGuideActive || attendanceGuideDone) return;
    if (trainingActive) {
      advanceTrainingSubstep();
      return;
    }
    skipStep?.("attendance", { force: true });
  }, [
    advanceTrainingSubstep,
    attendanceGuideActive,
    attendanceGuideDone,
    skipStep,
    trainingActive,
  ]);

  const setGuideSubstep = useCallback(
    (subId) => {
      if (trainingActive) setTrainingSubstep("attendance", subId);
      else setSubstep("attendance", subId);
    },
    [trainingActive, setTrainingSubstep, setSubstep],
  );
  const navigate = useNavigate();
  const scheduleUrl = useHubScheduleUrl();
  const isApp = useAdminHubUi();
  const trainerProfile = useSelector((state) => state.auth.trainerProfile);
  const tenantKey = useSelector((state) => state.auth.tenant?.id || "default");
  const {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncError,
    refreshPending,
    syncNow,
  } = useJournalOffline(tenantKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get("date") || toDateInputValue();
  const sessionFromUrl =
    searchParams.get("session") || searchParams.get("sessionId") || "";
  const groupFromUrl = searchParams.get("group") || "";

  const [sessions, setSessions] = useState([]);
  const [weekDayMarkers, setWeekDayMarkers] = useState({
    has: new Set(),
    needs: new Set(),
  });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(sessionFromUrl);
  const [groupFilter, setGroupFilter] = useState(groupFromUrl);
  const [journal, setJournal] = useState(null);
  const [journalLoading, setJournalLoading] = useState(false);
  const [absenceModal, setAbsenceModal] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingPatientId, setMarkingPatientId] = useState(null);

  const guideEmptySkip =
    attendanceGuideActive &&
    !attendanceGuideDone &&
    attendanceSubstepId === "att_mark" &&
    journal &&
    !(journal.attendances || []).length &&
    !journal.booking_mode &&
    !journal.group_archived
      ? skipEmptyAttendanceGuide
      : null;

  const setDate = (d) => {
    const next = new URLSearchParams(searchParams);
    next.set("date", d);
    setSearchParams(next, { replace: true });
  };

  const clearSession = () => {
    setActiveSessionId("");
    setJournal(null);
    const next = new URLSearchParams(searchParams);
    next.delete("session");
    setSearchParams(next, { replace: true });
  };

  const selectSession = (sessionId) => {
    setActiveSessionId(sessionId);
    const next = new URLSearchParams(searchParams);
    next.set("session", sessionId);
    setSearchParams(next, { replace: true });
    // Только переход на подсказку «галочка» — не advanceSubstep (иначе двойной шаг закрывает тур)
    if (attendanceGuideActive) {
      setGuideSubstep("att_mark");
    }
  };

  const loadJournalData = useCallback(
    async (sessionId) => {
      if (!sessionId) {
        setJournal(null);
        return null;
      }
      setJournalLoading(true);
      try {
        if (!navigator.onLine) {
          const cached = await readCachedJournal(tenantKey, sessionId);
          if (cached) {
            setJournal(cached);
            return cached;
          }
          toast.error("Нет кэша — откройте журнал при подключении к сети");
          setJournal(null);
          return null;
        }
        const data = await clinicAPI.getSessionJournal(sessionId);
        setJournal(data.session);
        await cacheJournal(tenantKey, sessionId, data.session);
        return data.session;
      } catch (e) {
        const cached = await readCachedJournal(tenantKey, sessionId);
        if (cached) {
          setJournal(cached);
          toast.info("Показаны сохранённые данные");
          return cached;
        }
        toast.error(e.response?.data?.error || "Ошибка журнала");
        setJournal(null);
        return null;
      } finally {
        setJournalLoading(false);
      }
    },
    [tenantKey, toast],
  );

  const patchSessionRollCall = useCallback((sessionId, statsSource) => {
    if (!sessionId || !statsSource) return;
    const roll = rollCallStatsFromAttendances(
      statsSource.attendances,
      statsSource,
    );
    const bookingMode = Boolean(
      statsSource.booking_mode || Number(statsSource.rsvp_capacity) > 0,
    );
    setSessions((prev) =>
      prev.map((s) =>
        String(s.id) === String(sessionId)
          ? {
              ...s,
              marked_count: roll.marked,
              present_count: roll.present,
              enrolled_count: bookingMode
                ? statsSource.rsvp_coming_count ??
                  statsSource.enrolled_count ??
                  roll.total
                : roll.total,
              expected_count: bookingMode
                ? statsSource.rsvp_coming_count ??
                  statsSource.expected_count ??
                  roll.total
                : roll.total,
              needs_attendance:
                roll.total > 0 &&
                roll.marked < roll.total &&
                Boolean(statsSource.needs_attendance),
              attendance_complete: roll.total > 0 && roll.marked >= roll.total,
              booking_mode: bookingMode || s.booking_mode,
              rsvp_capacity: statsSource.rsvp_capacity ?? s.rsvp_capacity,
              rsvp_coming_count:
                statsSource.rsvp_coming_count ?? s.rsvp_coming_count,
            }
          : s,
      ),
    );
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const grid = monthGridDays(date);
      const params = { from: grid[0], to: grid[grid.length - 1] };
      if (groupFilter) params.group_id = groupFilter;
      if (staffMode && trainerProfile?.id) {
        params.trainer_id = trainerProfile.id;
      }
      if (!navigator.onLine) {
        const cached = await readCachedSessions(tenantKey, date, groupFilter);
        setSessions(cached || []);
        setWeekDayMarkers({ has: new Set(), needs: new Set() });
        return;
      }
      const data = await clinicAPI.getTrainingSessions(params);
      const all = data.sessions || [];
      const list = all.filter(
        (s) => (s.start_datetime || "").slice(0, 10) === date,
      );
      setSessions(list);
      await cacheSessions(tenantKey, date, groupFilter, list);
      setWeekDayMarkers(weekMarkersFromSessions(all));
    } catch (e) {
      const cached = await readCachedSessions(tenantKey, date, groupFilter);
      if (cached) {
        setSessions(cached);
      } else {
        toast.error(e.response?.data?.error || "Ошибка загрузки");
        setSessions([]);
      }
      setWeekDayMarkers({ has: new Set(), needs: new Set() });
    } finally {
      setLoading(false);
    }
  }, [date, groupFilter, staffMode, trainerProfile?.id, tenantKey, toast]);

  useEffect(() => {
    loadSessions();
    clinicAPI.getSpecializations().then((gr) => {
      setGroups((gr.specializations || []).filter((g) => g.is_active !== false));
    });
  }, [loadSessions]);

  useEffect(() => {
    if (sessionFromUrl) setActiveSessionId(sessionFromUrl);
  }, [sessionFromUrl]);

  useEffect(() => {
    setGroupFilter(groupFromUrl);
  }, [groupFromUrl]);

  useEffect(() => {
    loadJournalData(activeSessionId);
  }, [activeSessionId, loadJournalData]);

  useEffect(() => {
    if (isApp) return;
    if (loading) return;
    if (
      attendanceGuideActive &&
      attendanceSubstepId === "att_open" &&
      !activeSessionId
    ) {
      return;
    }

    if (activeSessionId) {
      const valid = sessions.some(
        (x) => String(x.id) === String(activeSessionId),
      );
      if (!valid) {
        if (sessions.length > 0) {
          const pick =
            sessions.find((s) => s.needs_attendance) || sessions[0];
          selectSession(pick.id);
        } else {
          clearSession();
        }
      }
      return;
    }

    if (sessions.length > 0) {
      const pick = sessions.find((s) => s.needs_attendance) || sessions[0];
      selectSession(pick.id);
    }
  }, [isApp, loading, sessions, activeSessionId, date, attendanceGuideActive, attendanceSubstepId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!attendanceGuideActive || attendanceGuideDone || loading) return;
    if (sessions.length > 0) return;
    const markers = weekDayMarkers.has;
    if (!markers?.size) return;
    const today = toDateInputValue();
    const sorted = [...markers].sort();
    const pick = sorted.find((d) => d >= today) || sorted[sorted.length - 1];
    if (pick && pick !== date) setDate(pick);
  }, [
    attendanceGuideActive,
    attendanceGuideDone,
    loading,
    sessions.length,
    weekDayMarkers,
    date,
  ]);

  const handleSync = useCallback(async () => {
    const result = await syncNow();
    if (result.synced > 0) {
      if (activeSessionId) await loadJournalData(activeSessionId);
      loadSessions();
      toast.success(`Синхронизировано: ${result.synced}`);
    }
  }, [syncNow, activeSessionId, loadJournalData, loadSessions, toast]);

  const prevPendingRef = useRef(pendingCount);
  useEffect(() => {
    if (
      prevPendingRef.current > 0 &&
      pendingCount === 0 &&
      isOnline &&
      activeSessionId
    ) {
      loadJournalData(activeSessionId);
      loadSessions();
    }
    prevPendingRef.current = pendingCount;
  }, [
    pendingCount,
    isOnline,
    activeSessionId,
    loadJournalData,
    loadSessions,
  ]);

  useAdminRefresh(() => {
    loadSessions();
    if (activeSessionId) loadJournalData(activeSessionId);
  });

  const offlineBanner = (
    <JournalOfflineBanner
      isOnline={isOnline}
      pendingCount={pendingCount}
      isSyncing={isSyncing}
      lastSyncError={lastSyncError}
      onSync={handleSync}
    />
  );

  const mark = async (
    patientId,
    status,
    absence_reason,
    attendanceRow,
    sessionIdOverride,
  ) => {
    const sessionId = sessionIdOverride || activeSessionId;
    if (!sessionId) return;
    if (markingPatientId != null) return;
    if (!sessionIdOverride && !canMarkSessionAttendance(journal)) {
      toast.error("Занятие отменено");
      return;
    }
    const row =
      attendanceRow ||
      (journal?.attendances || []).find((a) => a.patient_id === patientId);
    const payload = row
      ? attendanceMarkPayload(row, status)
      : { status };
    const apiPayload = {
      ...payload,
      absence_reason: payload.clear ? undefined : absence_reason,
    };
    setMarkingPatientId(patientId);
    if (!sessionIdOverride && journal) {
      const optimistic = applyLocalAttendanceMark(
        journal,
        patientId,
        status,
        absence_reason,
        payload,
      );
      setJournal(optimistic);
      patchSessionRollCall(sessionId, optimistic);
      await cacheJournal(tenantKey, sessionId, optimistic);
    }

    const queueAndNotify = async () => {
      await enqueueMutation(tenantKey, {
        sessionId,
        patientId,
        payload: apiPayload,
      });
      await refreshPending();
      toast.success(
        navigator.onLine ? "Сохранено локально, отправляем…" : "Сохранено офлайн",
      );
    };

    try {
      if (!navigator.onLine) {
        await queueAndNotify();
        if (payload.status === "present" && !payload.clear) {
          refreshActivation?.();
          finishAttendanceMarkGuide();
        }
        return;
      }
      const data = await clinicAPI.markSessionAttendance(sessionId, {
        patient_id: patientId,
        ...apiPayload,
      });
      const err = data.attendance?.charge_error;
      if (payload.clear) toast.success("Отметка снята");
      else if (payload.status === "present" && err) toast.info(err);
      else if (data.auto_completed) toast.success("Перекличка завершена");
      else toast.success("Сохранено");
      if (payload.status === "present" && !payload.clear) {
        refreshActivation?.();
        finishAttendanceMarkGuide();
      }
      const updated = await loadJournalData(sessionId);
      if (updated) patchSessionRollCall(sessionId, updated);
      loadSessions();
    } catch (e) {
      if (!navigator.onLine || e.code === "ERR_NETWORK") {
        await queueAndNotify();
      } else {
        toast.error(e.response?.data?.error || "Ошибка");
        if (!sessionIdOverride) loadJournalData(sessionId);
      }
    } finally {
      setMarkingPatientId(null);
    }
  };

  const markAllWith = async (status, absence_reason) => {
    if (!activeSessionId || !journal) return;
    const pending = (journal.attendances || []).filter((a) =>
      status === "present"
        ? !attendanceMarkState(a).isPresent
        : !attendanceMarkState(a).isAbsent,
    );
    if (!pending.length) return;
    const payload =
      status === "present"
        ? { status: "present" }
        : { status: "absent", absence_reason };
    setMarkingAll(true);
    let nextJournal = journal;
    for (const a of pending) {
      nextJournal = applyLocalAttendanceMark(
        nextJournal,
        a.patient_id,
        status,
        absence_reason,
        payload,
      );
    }
    setJournal(nextJournal);
    patchSessionRollCall(activeSessionId, nextJournal);
    await cacheJournal(tenantKey, activeSessionId, nextJournal);

    const successMsg =
      status === "present" ? "Все отмечены" : "Все отмечены как «Не был»";

    try {
      if (!navigator.onLine) {
        for (const a of pending) {
          await enqueueMutation(tenantKey, {
            sessionId: activeSessionId,
            patientId: a.patient_id,
            payload,
          });
        }
        await refreshPending();
        toast.success("Все отмечены офлайн");
        return;
      }
      let autoCompleted = false;
      let chargeWarn = 0;
      let okCount = 0;
      let failCount = 0;
      for (const a of pending) {
        try {
          const data = await clinicAPI.markSessionAttendance(activeSessionId, {
            patient_id: a.patient_id,
            ...payload,
          });
          okCount += 1;
          if (data?.auto_completed) autoCompleted = true;
          if (data?.attendance?.charge_error) chargeWarn += 1;
        } catch {
          failCount += 1;
        }
      }
      if (failCount) {
        const updated = await loadJournalData(activeSessionId);
        if (updated) patchSessionRollCall(activeSessionId, updated);
        loadSessions();
        if (okCount) {
          toast.error(
            `Отмечено ${okCount} из ${pending.length}. Повторите для остальных.`,
          );
        } else {
          toast.error("Не удалось отметить");
        }
        return;
      }
      const updated = await loadJournalData(activeSessionId);
      if (updated) patchSessionRollCall(activeSessionId, updated);
      loadSessions();
      if (chargeWarn) {
        toast.info(
          `Отмечены все. У ${chargeWarn} не списалось занятие с абонемента — проверьте оплату.`,
        );
      } else {
        toast.success(
          autoCompleted && status === "present"
            ? "Перекличка завершена"
            : successMsg,
        );
      }
      if (status === "present") {
        refreshActivation?.();
        finishAttendanceMarkGuide();
      }
    } catch (e) {
      if (!navigator.onLine || e.code === "ERR_NETWORK") {
        for (const a of pending) {
          await enqueueMutation(tenantKey, {
            sessionId: activeSessionId,
            patientId: a.patient_id,
            payload,
          });
        }
        await refreshPending();
        toast.success("Сохранено офлайн, отправим при связи");
      } else {
        toast.error(e.response?.data?.error || "Ошибка");
        loadJournalData(activeSessionId);
      }
    } finally {
      setMarkingAll(false);
    }
  };

  const markAllPresent = () => markAllWith("present");
  const markAllAbsent = (reason) => markAllWith("absent", reason);

  const setGroupFilterAndUrl = (id) => {
    setGroupFilter(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("group", id);
    else next.delete("group");
    setSearchParams(next, { replace: true });
  };

  const groupChips = [
    { id: "", label: "Все" },
    ...groups.map((g) => ({ id: String(g.id), label: g.name })),
  ];

  const markDisabled =
    !journal ||
    journal.journal_readonly === true ||
    !canMarkSessionAttendance(journal);

  const sessionLiveStats = (sess) =>
    journal && String(sess.id) === String(activeSessionId)
      ? rollCallStatsFromAttendances(journal.attendances, journal)
      : null;

  const refreshCompetitionJournal = useCallback(() => {
    if (activeSessionId) loadJournalData(activeSessionId);
  }, [activeSessionId, loadJournalData]);

  const absenceSheet = (
    <AttendanceAbsenceSheet
      open={Boolean(absenceModal)}
      onClose={() => setAbsenceModal(null)}
      bulk={Boolean(absenceModal?.bulk)}
      subtitle={absenceModal?.bulk ? "Вся группа" : absenceModal?.name}
      onPick={(reason) => {
        if (absenceModal?.bulk) {
          markAllAbsent(reason);
          return;
        }
        const row = (journal?.attendances || []).find(
          (x) => x.patient_id === absenceModal?.patientId,
        );
        mark(absenceModal.patientId, "absent", reason, row);
      }}
    />
  );

  if (isApp) {
    if (activeSessionId) {
      const sess =
        journal ||
        sessions.find((x) => String(x.id) === String(activeSessionId));
      const title = sess ? sessionLabel(sess) : L.journal;

      return (
        <>
          <HubScreen
            title={title}
            onBack={clearSession}
            bodyClassName={aa.journalHubBody}
          >
            <ActivationPageGuide stepIds={["attendance"]} />
            {offlineBanner}
            {journalLoading && !journal ? (
              <p className={aa.journalHint}>Загрузка…</p>
            ) : !journal ? (
              <EmptyState title="Занятие не найдено" />
            ) : (
              <JournalAttendanceView
                journal={journal}
                mark={mark}
                markDisabled={markDisabled}
                onAbsence={setAbsenceModal}
                onMarkAll={markAllPresent}
                onMarkAllAbsent={() => setAbsenceModal({ bulk: true })}
                markingAll={markingAll}
                markingPatientId={markingPatientId}
                onCompetitionResultsChange={refreshCompetitionJournal}
                onSessionFeePaid={() => {
                  if (activeSessionId) loadJournalData(activeSessionId);
                }}
                guideEmptySkip={guideEmptySkip}
              />
            )}
          </HubScreen>
          {absenceSheet}
        </>
      );
    }

    return (
      <>
        <HubTabScreen
          title={L.journal}
          bodyClassName={aa.journalHubBody}
        >
          <ActivationPageGuide stepIds={["attendance"]} />
          {offlineBanner}
          <JournalWeekPicker
            date={date}
            onPick={setDate}
            daysWithSessions={weekDayMarkers.has}
            daysNeedingAttendance={weekDayMarkers.needs}
          />

          {groups.length > 0 ? (
            <div className={aa.journalFiltersWrap}>
              <HubFilterChips
                tabs={groupChips}
                active={String(groupFilter)}
                onChange={setGroupFilterAndUrl}
              />
            </div>
          ) : null}

          <div className={aa.journalSessionList}>
            {loading ? (
              <p className={aa.journalHint}>Загрузка занятий…</p>
            ) : sessions.length === 0 ? (
              <EmptyState
                title="Нет занятий на этот день"
                description="Проверьте расписание группы или выберите другую дату"
                actionLabel="Календарь"
                onAction={() => navigate(scheduleUrl({ view: "day", date }))}
              />
            ) : (
              <div className={aa.journalSessionGroup}>
                {sessions.map((sess, index) => (
                  <JournalSessionCard
                    key={sess.id}
                    sess={sess}
                    isLast={index === sessions.length - 1}
                    onOpen={() => selectSession(sess.id)}
                    liveStats={sessionLiveStats(sess)}
                  />
                ))}
              </div>
            )}
          </div>
        </HubTabScreen>
        {absenceSheet}
      </>
    );
  }

  return (
    <div className={md.desktopJournalPage}>
      <ActivationPageGuide stepIds={["attendance"]} />
      {offlineBanner}

      <div className={md.desktopJournalPanel}>
        <div className={md.desktopJournalHead}>
          <h1 className={md.desktopJournalHeadTitle}>{L.journal}</h1>
        </div>

        <div className={md.desktopJournalSplit}>
          <aside className={md.desktopJournalSidebar}>
            <JournalMiniMonth
              date={date}
              onPick={setDate}
              daysWithSessions={weekDayMarkers.has}
              daysNeedingAttendance={weekDayMarkers.needs}
            />

            {groups.length > 0 ? (
              <div className={md.desktopJournalSidebarFilters}>
                <HubFilterChips
                  tabs={groupChips}
                  active={String(groupFilter)}
                  onChange={setGroupFilterAndUrl}
                />
              </div>
            ) : null}

            <div className={md.desktopJournalSessions}>
              <h3 className={md.desktopJournalSideTitle}>
                {formatJournalHeader(date).title}
              </h3>
              <div className={md.desktopJournalSessionsList}>
                {loading ? (
                  <p className={aa.journalHint}>Загрузка…</p>
                ) : sessions.length === 0 ? (
                  <p className={md.desktopJournalSideEmpty}>
                    Нет занятий на этот день
                  </p>
                ) : (
                  <div className={md.journalDeskSessionList}>
                    {sessions.map((sess) => (
                      <JournalSessionCard
                        key={sess.id}
                        sess={sess}
                        onOpen={() => selectSession(sess.id)}
                        desktop
                        active={
                          String(sess.id) === String(activeSessionId)
                        }
                        liveStats={sessionLiveStats(sess)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <main className={md.desktopJournalMain}>
            {journalLoading && activeSessionId && !journal ? (
              <p className={aa.journalHint}>Загрузка переклички…</p>
            ) : activeSessionId && journal ? (
              <JournalAttendanceView
                journal={journal}
                mark={mark}
                markDisabled={markDisabled}
                onAbsence={setAbsenceModal}
                onMarkAll={markAllPresent}
                onMarkAllAbsent={() => setAbsenceModal({ bulk: true })}
                markingAll={markingAll}
                markingPatientId={markingPatientId}
                onCompetitionResultsChange={refreshCompetitionJournal}
                onSessionFeePaid={() => {
                  if (activeSessionId) loadJournalData(activeSessionId);
                }}
                desktop
                guideEmptySkip={guideEmptySkip}
              />
            ) : !loading && sessions.length === 0 ? (
              <div className={md.desktopJournalPlaceholder}>
                <p className={md.desktopJournalPlaceholderTitle}>
                  Нет занятий на этот день
                </p>
                <p className={md.desktopJournalPlaceholderHint}>
                  Проверьте расписание или выберите другую дату в календаре
                </p>
                <button
                  type="button"
                  className={md.desktopJournalPlaceholderBtn}
                  onClick={() =>
                    navigate(scheduleUrl({ view: "day", date }))
                  }
                >
                  Открыть календарь
                </button>
              </div>
            ) : (
              <div className={md.desktopJournalPlaceholder}>
                <p className={md.desktopJournalPlaceholderTitle}>
                  Выберите занятие
                </p>
                <p className={md.desktopJournalPlaceholderHint}>
                  Слева список занятий на выбранный день — откройте нужное для
                  переклички
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {absenceSheet}
    </div>
  );
}
